
import type { AvatarComposition, AvatarItem } from '../types/types';
import { prepareImageForUpload } from '../utils/imageOptimization';
import { api } from '../../../services/api';
import avatarDefault from 'assets/avatar-editor/avatar_default.png';

interface KonvaStage {
  toDataURL(config?: {
    mimeType?: string;
    quality?: number;
    pixelRatio?: number;
  }): string;
}

export const API_CONFIG = {
  baseURL: 'https://service2.funifier.com/v3',
  saveEndpoint: '/database/user_avatar__c',
  token: 'Basic NjRjNjgzYmNjMjdiZmYxNDM5Yjc0ZTk1OjYzYzliNjc2ZDMxODI0MDdkZGVmZGIyYw=='
};

export interface UserAvatarSave {
  _id: string; 
  userId: string; 
  avatarName: string ; 
  avatarImageUrl: string; 
  skinColor: string; 
  avatarCategories: {
    [category: string]: {
      itemId: string | null; 
      itemName: string | null; 
      itemImageUrl: string | null; 
      position: { x: number; y: number } | null; 
      scale: number | null; 
      rotation: number | null; 
      color: string | null; 
    };
  };
  created?: { $date: number }; 
  updated: { $date: number };
}

const ALL_CATEGORIES = [
  'hair',
  'face', 
  'shirt',
  'pants',
  'shoes',
  'accessories',
  'hat',
  'glasses',
  'bag'
];

const DEFAULT_AVATAR_URL = avatarDefault;

export function getCurrentUserId(): string | null {
  try {
    const userData = localStorage.getItem('user');
    if (userData) {
      const user = JSON.parse(userData);
      if (user && user._id) {
        return user._id;
      }
    }
    
    const alternativeUserData = localStorage.getItem('teensmart-user') || 
                               localStorage.getItem('funifier-user') ||
                               sessionStorage.getItem('user');
    
    if (alternativeUserData) {
      const user = JSON.parse(alternativeUserData);
      if (user && user._id) {
        return user._id;
      }
    }
  } catch (error) {
  }
  
  return null;
}

export function getCurrentUserData(): any | null {
  try {
    const userData = localStorage.getItem('user');
    if (userData) {
      const user = JSON.parse(userData);
      if (user && user._id) {
        return user;
      }
    }
    
    const alternativeUserData = localStorage.getItem('@TeenSmart:user');
    if (alternativeUserData) {
      const user = JSON.parse(alternativeUserData);
      if (user && user._id) {
        return user;
      }
    }
  } catch (error) {
  }
  
  return null;
}

export async function captureAvatarImage(stage: KonvaStage): Promise<string> {
  try {
    
    const dataURL = stage.toDataURL({
      mimeType: 'image/png',
      quality: 1.0,
      pixelRatio: 2 
    });
    
        return dataURL;
  } catch (error) {
        throw new Error('Falha ao capturar imagem do avatar');
  }
}

export async function captureBustImage(stage: KonvaStage): Promise<string> {
  try {
    const fullImageDataURL = stage.toDataURL({
      mimeType: 'image/png',
      quality: 1.0,
      pixelRatio: 2
    });

    return new Promise<string>((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            throw new Error('Não foi possível criar contexto do canvas');
          }

          const scale = 2;
          const outputSize = Math.floor(img.width * 0.5);
          const cropX = Math.floor(img.width * 0.17);
          const cropY = Math.floor(img.height * 0.05);
          const cropWidth = outputSize;
          const cropHeight = outputSize;
          
          canvas.width = outputSize;
          canvas.height = outputSize;
          
          ctx.drawImage(
            img,
            cropX, cropY, cropWidth, cropHeight,
            0, 0, outputSize, outputSize
          );
          
          const croppedDataURL = canvas.toDataURL('image/jpeg', 0.95);
          
          resolve(croppedDataURL);
        } catch (error) {
          reject(new Error('Falha ao recortar imagem do busto'));
        }
      };
      
      img.onerror = () => {
        reject(new Error('Falha ao carregar imagem para recorte'));
      };
      
      img.src = fullImageDataURL;
    });
  } catch (error) {
    throw new Error('Falha ao capturar imagem do busto');
  }
}

async function uploadImageToS3(imageDataURL: string, fileName: string): Promise<string> {
  try {
        
    const { optimizedDataURL, finalFileName, compressionApplied } = await prepareImageForUpload(
      imageDataURL, 
      fileName
    );
    
    if (compressionApplied) {
          }
    
        
    const base64Data = optimizedDataURL.replace(/^data:image\/[a-z]+;base64,/, '');
    
    
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'image/png' });

    
    const extraConfig = {
      session: "avatar",
      thumbnails: [
        { name: "small", width: 160, height: 160 },
        { name: "medium", width: 260, height: 260 }
      ],
      transform: [
        { stage: "size", width: 400, height: 400 }, 
        { stage: "outputFormat", format: "png" }
      ]
    };

    
    const formData = new FormData();
    formData.append('file', blob, finalFileName);
    formData.append('extra', JSON.stringify(extraConfig));
    
            
    const uploadResponse = await fetch(`${API_CONFIG.baseURL}/upload/image`, {
      method: 'POST',
      headers: {
        'Authorization': API_CONFIG.token,
        
      },
      body: formData
    });
    
    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
            throw new Error(`Erro no upload: ${uploadResponse.status} - ${errorText}`);
    }
    
    const uploadResult = await uploadResponse.json();
        
    
    if (!uploadResult.uploads || !Array.isArray(uploadResult.uploads)) {
            throw new Error('Formato de resposta inválido - uploads não encontrados');
    }
    
    
    const originalUpload = uploadResult.uploads.find((upload: any) => upload.thumb === 'original');
    
    if (!originalUpload || !originalUpload.url) {
            throw new Error('URL da imagem original não foi encontrada');
    }
    
    const s3Url = originalUpload.url;
    
            return s3Url;
    
  } catch (error) {
        
        
    try {
      const { optimizedDataURL } = await prepareImageForUpload(imageDataURL, fileName);
      
      
      if (optimizedDataURL.length < 5000000) { 
                return optimizedDataURL;
      }
    } catch (optimizationError) {
          }
    
    
    if (imageDataURL.length < 3000000) { 
            return imageDataURL;
    }
    
    
        const fallbackUrl = `https://funifier-avatars.s3.amazonaws.com/user-avatars/${fileName}`;
    
    return fallbackUrl;
  }
}

function dataURIToBlob(dataURI: string): Blob {
  const splitDataURI = dataURI.split(",");
  const byteString =
    splitDataURI[0].indexOf("base64") >= 0
      ? atob(splitDataURI[1])
      : decodeURI(splitDataURI[1]);
  const mimeString = splitDataURI[0].split(":")[1].split(";")[0];

  const ia = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new Blob([ia], { type: mimeString });
}

async function uploadProfilePicture(imageDataURL: string, userId: string): Promise<{
  small: { url: string };
  medium: { url: string };
  original: { url: string };
} | null> {
  try {
    const formData = new FormData();
    formData.append('file', new File([dataURIToBlob(imageDataURL)], 'profile.jpg'));
    formData.append(
      'extra',
      JSON.stringify({
        session: "avatar",
        thumbnails: [
          { name: "small", width: 160, height: 160 },
          { name: "medium", width: 260, height: 260 }
        ]
      })
    );

    const { data } = await api.post('upload/file', formData);

    if (data.status === 'OK' && data.uploads && data.uploads.length > 0) {
      const uploads = data.uploads;
      const smallUpload = uploads.find((u: any) => u.thumb === 'small') || uploads[0];
      const mediumUpload = uploads.find((u: any) => u.thumb === 'medium') || uploads[0];
      const originalUpload = uploads.find((u: any) => u.thumb === 'original') || uploads[0];
      
      return {
        small: { url: smallUpload.url },
        medium: { url: mediumUpload.url },
        original: { url: originalUpload.url }
      };
    }

    return null;
  } catch (error) {
    return null;
  }
}

async function checkExistingAvatar(userId: string): Promise<boolean> {
  try {
    const response = await fetch(
      `${API_CONFIG.baseURL}${API_CONFIG.saveEndpoint}?q=_id:'${userId}'`,
      {
        method: 'GET',
        headers: {
          'Authorization': API_CONFIG.token,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        }
      }
    );
    
    if (!response.ok) {
      return false;
    }
    
    const result = await response.json();
    const exists = Array.isArray(result) ? result.length > 0 : false;
    return exists;
  } catch (error) {
    return false;
  }
}

function extractSkinColor(avatarComposition: AvatarComposition): string {
  
  const skinItem = avatarComposition.find(item => 
    item.category === 'skin' || item.category === 'base'
  );
  
  if (skinItem && skinItem.skinColor) {
        return skinItem.skinColor;
  }
  
    return '#f67e57'; 
}

function prepareAvatarCategories(avatarComposition: AvatarComposition): UserAvatarSave['avatarCategories'] {
  const categories: UserAvatarSave['avatarCategories'] = {};
  
  
  ALL_CATEGORIES.forEach(category => {
    categories[category] = {
      itemId: null,
      itemName: null,
      itemImageUrl: null,
      position: null,
      scale: null,
      rotation: null,
      color: null
    };
  });
  
  
  avatarComposition.forEach(item => {
    
    if (item.category !== 'skin') { 
      categories[item.category] = {
        itemId: item.id,
        itemName: item.title || item.id,
        itemImageUrl: item.src,
        position: { x: item.x, y: item.y },
        scale: item.scale || 1,
        rotation: item.rotation || 0,
        color: item.color || '#ffffff'
      };
    }
  });
  
  return categories;
}

async function updateUserProfileImage(
  userData: any,
  profileImageData: {
    small: { url: string };
    medium: { url: string };
    original: { url: string };
  }
): Promise<any> {
  try {
    const userId = userData._id;
    
    if (!userId) {
      throw new Error('ID do usuário não encontrado');
    }
    
    const { data: currentProfile } = await api.get(`player/${userId}`);
    
    const updatedProfile = {
      ...currentProfile,
      image: profileImageData
    };
    
    if (!updatedProfile.extra) {
      updatedProfile.extra = {};
    }
    
    const { data: updatedData } = await api.put(`player/${userId}`, updatedProfile);
    
    try {
      const updatedUserData = { ...currentProfile, image: profileImageData };
      localStorage.setItem('user', JSON.stringify(updatedUserData));
    } catch (storageError) {
    }
    
    return updatedData;
  } catch (error) {
    throw error;
  }
}

export async function saveUserAvatar(
  avatarComposition: AvatarComposition,
  stage: KonvaStage,
  avatarName = 'Meu Avatar',
  userId?: string
): Promise<{ success: boolean; avatarId?: string; error?: string }> {
  try {
    const currentUserId = userId || getCurrentUserId();
    
    if (!currentUserId) {
      throw new Error('Usuário não identificado. Faça login no sistema TeenSmart para salvar o avatar.');
    }
    
    let userData = null;
    try {
      const { data } = await api.get(`player/${currentUserId}`);
      userData = data;
    } catch (error) {
      throw new Error('Não foi possível obter dados do usuário atual');
    }
    
    const avatarImageDataURL = await captureAvatarImage(stage);
    
    const fileName = `avatar_${currentUserId}_${Date.now()}.png`;
    const avatarImageUrl = await uploadImageToS3(avatarImageDataURL, fileName);
    
    let profileImageData = null;
    try {
      const bustImageDataURL = await captureBustImage(stage);
      profileImageData = await uploadProfilePicture(bustImageDataURL, currentUserId);
    } catch (bustError) {
    }
    
    const avatarCategories = prepareAvatarCategories(avatarComposition);
    
    
    const avatarExists = await checkExistingAvatar(currentUserId);
    const httpMethod = avatarExists ? 'PUT' : 'POST';
    
    
    const skinColor = extractSkinColor(avatarComposition);
    
    
    const userAvatarData: UserAvatarSave = {
      _id: currentUserId, 
      userId: currentUserId,
      avatarName,
      avatarImageUrl,
      skinColor, 
      avatarCategories,
      updated: { $date: Date.now() }
    };
    
    
    if (!avatarExists) {
      userAvatarData.created = { $date: Date.now() };
    }
    
        
    const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.saveEndpoint}`, {
      method: httpMethod,
      headers: {
        'Authorization': API_CONFIG.token,
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache'
      },
      body: JSON.stringify(userAvatarData)
    });
    
    if (!response.ok) {
      throw new Error(`Erro na API: ${response.status} ${response.statusText}`);
    }
    
    const result = await response.json();
    
    if (profileImageData && userData) {
      try {
        if (userData._id !== currentUserId) {
          userData._id = currentUserId;
        }
        
        await updateUserProfileImage(userData, profileImageData);
      } catch (profileError) {
      }
    }
    
    return {
      success: true,
      avatarId: currentUserId
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro desconhecido'
    };
  }
}

export async function loadCurrentUserAvatar(userId?: string): Promise<UserAvatarSave | null> {
  try {
    const currentUserId = userId || getCurrentUserId();
    
    const response = await fetch(
      `${API_CONFIG.baseURL}${API_CONFIG.saveEndpoint}?q=_id:'${currentUserId}'`,
      {
        method: 'GET',
        headers: {
          'Authorization': API_CONFIG.token,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        }
      }
    );
    
    if (!response.ok) {
      return null;
    }
    
    const result = await response.json();
    const avatar = Array.isArray(result) && result.length > 0 ? result[0] : null;
    return avatar;
  } catch (error) {
    return null;
  }
}

export function convertSavedAvatarToComposition(savedAvatar: UserAvatarSave): AvatarComposition {
  const composition: AvatarComposition = [];
  
  Object.entries(savedAvatar.avatarCategories).forEach(([category, categoryData]) => {
    if (categoryData.itemId && categoryData.itemImageUrl && categoryData.position) {
      const avatarItem: AvatarItem = {
        id: categoryData.itemId,
        src: categoryData.itemImageUrl,
        x: categoryData.position.x,
        y: categoryData.position.y,
        zIndex: getZIndexForCategory(category), 
        category: category,
        scale: categoryData.scale || 1,
        rotation: categoryData.rotation || 0,
        color: categoryData.color || '#ffffff',
        title: categoryData.itemName || categoryData.itemId,
        
        skinColor: category === 'base' ? savedAvatar.skinColor : undefined
      };
      
      composition.push(avatarItem);
    }
  });
  
  
  const hasBaseItem = composition.some(item => item.category === 'base');
  if (!hasBaseItem && savedAvatar.skinColor) {
        
    let skinSrc = '/body/boneco.png'; 
    let skinId = 'skin_default';
    
    if (savedAvatar.skinColor === '#8b5a42') {
      skinSrc = '/body/Avatar_pardo.png';
      skinId = 'skin_claro';
    } else if (savedAvatar.skinColor === '#f4a688') {
      skinSrc = '/body/Avatar_branco.png';
      skinId = 'skin_rosado';
    } else if (savedAvatar.skinColor === '#e8b894') {
      skinSrc = '/body/Avatar_amarelo.png';
      skinId = 'skin_bege';
    }
    
    const baseItem: AvatarItem = {
      id: skinId,
      src: skinSrc,
      x: 200,
      y: 250,
      zIndex: 0,
      category: 'base',
      scale: 1,
      skinColor: savedAvatar.skinColor
    };
    
    composition.unshift(baseItem); 
  }
  
    return composition;
}

function getZIndexForCategory(category: string): number {
  const zIndexMap: { [key: string]: number } = {
    'base': 1,     
    'face': 2,     
    'hair': 3,     
    'shirt': 4,    
    'pants': 5,    
    'shoes': 6,    
    'accessories': 7, 
    'hat': 8,      
    'glasses': 9,  
    'bag': 10      
  };
  
  return zIndexMap[category] || 5; 
}

function createDefaultAvatarData(userId: string): UserAvatarSave {
  return {
    _id: userId,
    userId: userId,
    avatarName: 'Avatar Padrão',
    avatarImageUrl: DEFAULT_AVATAR_URL,
    skinColor: '#f67e57', 
    avatarCategories: {
      base: {
        itemId: 'skin_default',
        itemName: 'Avatar Padrão',
        itemImageUrl: '/body/boneco.png',
        position: { x: 200, y: 250 },
        scale: 1,
        rotation: 0,
        color: '#ffffff'
      },
      hair: { itemId: null, itemName: null, itemImageUrl: null, position: null, scale: null, rotation: null, color: null },
      face: { itemId: null, itemName: null, itemImageUrl: null, position: null, scale: null, rotation: null, color: null },
      shirt: { itemId: null, itemName: null, itemImageUrl: null, position: null, scale: null, rotation: null, color: null },
      pants: { itemId: null, itemName: null, itemImageUrl: null, position: null, scale: null, rotation: null, color: null },
      shoes: { itemId: null, itemName: null, itemImageUrl: null, position: null, scale: null, rotation: null, color: null },
      accessories: { itemId: null, itemName: null, itemImageUrl: null, position: null, scale: null, rotation: null, color: null },
      hat: { itemId: null, itemName: null, itemImageUrl: null, position: null, scale: null, rotation: null, color: null },
      glasses: { itemId: null, itemName: null, itemImageUrl: null, position: null, scale: null, rotation: null, color: null },
      bag: { itemId: null, itemName: null, itemImageUrl: null, position: null, scale: null, rotation: null, color: null }
    },
    created: { $date: Date.now() },
    updated: { $date: Date.now() }
  };
}

export async function loadUserAvatarForEditing(userId?: string): Promise<{
  avatarComposition: AvatarComposition;
  avatarData: UserAvatarSave | null;
  hasExistingAvatar: boolean;
}> {
  try {
    const targetUserId = userId || getCurrentUserId();
    
    if (!targetUserId) {
            
      const defaultAvatar = createDefaultAvatarData('guest');
      const avatarComposition = convertSavedAvatarToComposition(defaultAvatar);
      
      return {
        avatarComposition,
        avatarData: defaultAvatar,
        hasExistingAvatar: false
      };
    }
    
    const userData = getCurrentUserData();
    
            
    const savedAvatar = await loadCurrentUserAvatar(targetUserId);
    
    if (savedAvatar) {
      
      const avatarComposition = convertSavedAvatarToComposition(savedAvatar);
      
            return {
        avatarComposition,
        avatarData: savedAvatar,
        hasExistingAvatar: true
      };
    } else {
      
            const defaultAvatar = createDefaultAvatarData(targetUserId);
      const avatarComposition = convertSavedAvatarToComposition(defaultAvatar);
      
      return {
        avatarComposition,
        avatarData: defaultAvatar,
        hasExistingAvatar: false
      };
    }
    
  } catch (error) {
    const defaultAvatar = createDefaultAvatarData('error');
    const avatarComposition = convertSavedAvatarToComposition(defaultAvatar);
    
    return {
      avatarComposition,
      avatarData: defaultAvatar,
      hasExistingAvatar: false
    };
  }
}

export async function loadUserAvatars(userId?: string): Promise<UserAvatarSave[]> {
  try {
    const currentUserId = userId || getCurrentUserId();
    
    const response = await fetch(
      `${API_CONFIG.baseURL}${API_CONFIG.saveEndpoint}?q=userId:'${currentUserId}'`,
      {
        method: 'GET',
        headers: {
          'Authorization': API_CONFIG.token,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        }
      }
    );
    
    if (!response.ok) {
      throw new Error(`Erro na API: ${response.status} ${response.statusText}`);
    }
    
    const avatars = await response.json();
    return Array.isArray(avatars) ? avatars : [];
  } catch (error) {
    return [];
  }
}
